import { useState } from "react"
import { createPortal } from "react-dom"
import Link from "next/link"
import cn from "classnames"
import { CarouselProvider, Slider, Slide, ButtonBack, ButtonNext } from "pure-react-carousel"
import richText from "utilities/richText"
import { getStoryblokLink } from "utilities/getStoryblokLink"
import useWindowSize from "utilities/useWindowSize"
import CallToAction from "components/CallToAction"
import Image from "components/Image"
import Arrow from "public/assets/chevron-teal.svg"
import Expand from "public/assets/expand.svg"
import GalleryModal from "components/GalleryModal"
import usePortalModal from "utilities/usePortalModal"

export default function LocationSection({ location, slug, idx }) {
  const width = useWindowSize().width
  const [selectedImg, setSelectedImg] = useState(0)

  const { ref, mounted, modalOpen, closeModal, focusRef } = usePortalModal([location._uid], () => location.photos)

  const buttonStyles = "mx-0 pt-0 absolute top-1/2 -translate-y-1/2"
  const isOddTile = idx % 2 !== 0
  const isEvenTile = idx % 2 === 0

  const moveRight = () =>
    selectedImg < location.photos.length - 1 ? setSelectedImg(selectedImg + 1) : setSelectedImg(0)

  const moveLeft = () =>
    selectedImg !== 0 ? setSelectedImg(selectedImg - 1) : setSelectedImg(location.photos.length - 1)

  return (
    <section className={cn("px-6 pb-10 lg:py-12", { "bg-stone-light": isOddTile })} id={slug}>
      <div className="relative mx-auto  max-w-screen-xl">
        <div className="flex flex-col lg:flex-row lg:gap-x-14">
          <div className={cn({ "lg:order-last": isOddTile })}>
            <Link href={`#${location._uid}`}>
              <div
                className={cn(
                  "relative mx-auto mb-6 h-[280px] w-full max-w-2xl overflow-hidden rounded-10 sm:h-[380px] lg:w-[590px]",
                )}
              >
                <Image
                  src={location.photos[selectedImg].filename}
                  alt={location.photos[selectedImg].alt}
                  placeholder={location.photos[selectedImg]?.blurDataURL ? "blur" : "empty"}
                  blurDataURL={location.photos[selectedImg]?.blurDataURL}
                  fill
                  style={{ objectFit: "cover" }}
                />
                <Expand
                  className={cn("absolute bottom-2", {
                    "right-2": isOddTile && width > 1024,
                    "left-2": isEvenTile || width < 1024,
                  })}
                />
              </div>
            </Link>
            <CarouselProvider
              isIntrinsicHeight
              totalSlides={location.photos.length}
              visibleSlides={width > 600 ? 4 : 3}
              infinite
              currentSlide={selectedImg}
              className={cn("relative max-w-2xl sm:mx-auto")}
            >
              <Slider className="mx-auto w-3/4 max-w-[500px] overflow-hidden md:w-full">
                {location.photos.map((photo, i) => (
                  <Slide key={i} index={i} className={cn("px-1 lg:px-2")}>
                    <button
                      onClick={() => setSelectedImg(location.photos.indexOf(photo))}
                      className="h-full w-full max-w-xs"
                    >
                      <Image
                        placeholder={photo.image?.blurDataURL ? "blur" : "empty"}
                        blurDataURL={photo.image?.blurDataURL}
                        src={photo.filename}
                        alt={photo.alt}
                        width={200}
                        height={175}
                        className={cn({ "border-[3px] border-green": i === selectedImg })}
                        style={{ objectFit: "cover", layout: "intrinsic" }}
                      />
                    </button>
                  </Slide>
                ))}
              </Slider>
              <div>
                <ButtonBack className={cn(buttonStyles, "left-2 lg:left-0")} onClick={moveLeft}>
                  <Arrow className="rotate-90" />
                </ButtonBack>
                <ButtonNext className={cn(buttonStyles, "right-2 lg:right-0")} onClick={moveRight}>
                  <Arrow className="-rotate-90" />
                </ButtonNext>
              </div>
            </CarouselProvider>
            {mounted && modalOpen
              ? createPortal(
                  <GalleryModal images={location.photos} closeModal={closeModal} focusRef={focusRef} />,
                  ref.current,
                )
              : null}
          </div>
          <div className="flex flex-col justify-center">
            <h2 className="text-teal-dark">{location.location_name}</h2>
            <CallToAction style="inline" href={getStoryblokLink(location.location_link)}>
              <div className="location-address relative my-2 pl-12 font-tertiary">
                <address className="max-w-xs not-italic">{location.map_pin.address}</address>
              </div>
            </CallToAction>
            <div className="description prose">{richText(location.description)}</div>
          </div>
        </div>
      </div>
    </section>
  )
}
